import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  Facebook,
  ArrowRight,
  Check
} from 'lucide-react';
import { businessInfo, orientationClass } from '../data/mock';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "contact",
        ...formData
      })
    })
      .then(() => {
        setSubmitted(true);
        setError(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      })
      .catch((err) => {
        console.error("Form submission error:", err);
        setError(true);
      });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#F7F8FA] via-white to-[#6E9EB9]/10 overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#6E9EB9]/10 rounded-full mb-6">
              <Mail size={16} className="text-[#6E9EB9]" />
              <span className="text-sm font-medium text-[#6E9EB9]">Get In Touch</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-[#4A5568] mb-8 max-w-3xl mx-auto">
              We'd love to hear from you. Reach out to schedule a consultation, 
              RSVP for an orientation class, or ask any questions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-serif font-medium text-[#2C2F36] mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-10">
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-start gap-4 p-4 rounded-xl bg-[#F7F8FA] hover:bg-[#6E9EB9]/5 transition-colors"
                >
                  <div className="p-3 bg-[#6E9EB9]/10 rounded-full">
                    <Phone size={24} className="text-[#6E9EB9]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2C2F36] mb-1">Phone</h3>
                    <p className="text-[#6E9EB9]">{businessInfo.phone}</p>
                  </div>
                </a>
                
                <a 
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-start gap-4 p-4 rounded-xl bg-[#F7F8FA] hover:bg-[#AF9EB8]/5 transition-colors"
                >
                  <div className="p-3 bg-[#AF9EB8]/10 rounded-full">
                    <Mail size={24} className="text-[#AF9EB8]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2C2F36] mb-1">Email</h3>
                    <p className="text-[#AF9EB8]">{businessInfo.email}</p>
                  </div>
                </a>
                
                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F7F8FA]">
                  <div className="p-3 bg-[#93A8C7]/10 rounded-full">
                    <MapPin size={24} className="text-[#93A8C7]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2C2F36] mb-1">Address</h3>
                    <p className="text-[#4A5568]">{businessInfo.address}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F7F8FA]">
                  <div className="p-3 bg-[#A08CA8]/10 rounded-full">
                    <Clock size={24} className="text-[#A08CA8]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2C2F36] mb-1">Hours</h3>
                    <div className="text-[#4A5568] text-sm space-y-1">
                      <p>Monday: {businessInfo.hours.monday}</p>
                      <p>Tuesday: {businessInfo.hours.tuesday}</p>
                      <p>Wednesday: {businessInfo.hours.wednesday}</p>
                      <p>Thursday: {businessInfo.hours.thursday}</p>
                      <p>Friday: {businessInfo.hours.friday}</p>
                      <p>Saturday: {businessInfo.hours.saturday}</p>
                      <p>Sunday: {businessInfo.hours.sunday}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mb-10">
                <h3 className="font-medium text-[#2C2F36] mb-4">Follow Us</h3>
                <a 
                  href={businessInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#6E9EB9]/10 rounded-full text-[#6E9EB9] hover:bg-[#6E9EB9]/20 transition-colors"
                >
                  <Facebook size={20} />
                  <span className="font-medium">Facebook</span>
                </a>
              </div>

              {/* Orientation Reminder */}
              <div className="bg-[#6E9EB9]/5 rounded-xl p-6 border border-[#6E9EB9]/20">
                <h3 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">
                  Free Orientation Classes
                </h3>
                <p className="text-[#4A5568] mb-4">
                  {orientationClass.schedule} at {orientationClass.time}
                </p>
                <Link to="/orientation" className="text-[#6E9EB9] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Learn More
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#F7F8FA] rounded-2xl p-8">
                <h2 className="text-2xl font-serif font-medium text-[#2C2F36] mb-6">
                  Send Us a Message
                </h2>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[#6E9EB9]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check size={32} className="text-[#6E9EB9]" />
                    </div>
                    <h3 className="text-xl font-serif font-medium text-[#2C2F36] mb-4">
                      Message Sent!
                    </h3>
                    <p className="text-[#4A5568] mb-6">
                      Thank you for reaching out. We'll get back to you as soon as possible.
                    </p>
                    <button 
                      onClick={() => setSubmitted(false)}
                      className="btn-secondary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form 
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <input type="hidden" name="bot-field" />
                    
                    {error && (
                      <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm">
                        There was an error submitting your message. Please try again or contact us directly.
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-[#2C2F36] mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="input-serenity"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[#2C2F36] mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="input-serenity"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-[#2C2F36] mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="input-serenity"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-[#2C2F36] mb-2">
                          Subject *
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="input-serenity"
                          required
                        >
                          <option value="">Select a subject</option>
                          <option value="consultation">Schedule a Consultation</option>
                          <option value="pure-renewal">Pure Renewal Protocol Inquiry</option>
                          <option value="orientation">RSVP for Orientation</option>
                          <option value="services">Question About Services</option>
                          <option value="classes">Classes & Events</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#2C2F36] mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="input-serenity resize-none"
                        required
                      />
                    </div>
                    
                    <button type="submit" className="btn-primary w-full">
                      <Send size={18} />
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-[#F7F8FA]">
        <div className="container-custom py-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-serif font-medium text-[#2C2F36] mb-4">
              Find Us in Gordon, Nebraska
            </h2>
            <p className="text-[#4A5568]">
              Located on Main Street in the heart of Gordon.
            </p>
          </div>
        </div>
        <div className="h-96">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2987.826728955098!2d-102.2063!3d42.8058!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x877e7c0fb6d04d99%3A0x2b5e5e5e5e5e5e5e!2s129%20N%20Main%20St%2C%20Gordon%2C%20NE%2069343!5e0!3m2!1sen!2sus!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Serenity Natural Health & Wellness Location"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6E9EB9] to-[#93A8C7] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
            Ready to Begin Your Wellness Journey?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            We look forward to supporting you on your path to optimal health.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${businessInfo.phone}`}
              className="px-8 py-4 bg-white text-[#6E9EB9] rounded-full font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              Call Now
            </a>
            <Link 
              to="/orientation" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Free Orientation Class
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;